import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AccountPageDocument,
  AddBankAccountMembershipMemberDocument,
  BankAccountMembershipMembersDocument,
} from 'generated/resources/graphql'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BankAccountMembershipSelector } from 'modules/banking/components/BankAccountMembershipSelector'

interface AddBankAccountMembershipModalProps {
  onClose: () => void
  bankAccountId: number
}

const AddBankAccountMembershipModal = ({
  onClose,
  bankAccountId,
}: AddBankAccountMembershipModalProps) => {
  const { company } = useCurrentSession()
  const [selectedEmployees, setSelectedEmployees] =
    React.useState<EmployeeForSelectFragmentNoAccess[]>()

  const [addMembership] = useMutation(AddBankAccountMembershipMemberDocument, {
    refetchQueries: [BankAccountMembershipMembersDocument, AccountPageDocument],
    extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountMembership,
  })

  const handleSelectEmployees = (newEmployees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(newEmployees)
  }

  const handleSubmit = async () => {
    track(trackingEvents.accounting.banking.addPeopleBankAccount)
    const result = await addMembership({
      variables: {
        employeeIds: selectedEmployees?.map((employee) => employee.id) || [],
        companyId: company.id,
        bankAccountId: bankAccountId,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      onClose()
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Modal
      title={i18n.t('banking.accounts.add_membership_modal.title')}
      description={i18n.t('banking.accounts.add_membership_modal.description')}
      illustration={'add-user'}
      onClose={onClose}
      footer={
        <Box flexDirection="row" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={selectedEmployees?.length == 0}>
            {i18n.t('common.add')}
          </Button>
        </Box>
      }
    >
      <Box flexDirection="column" gap="s24">
        <BankAccountMembershipSelector
          selectedEmployees={selectedEmployees}
          handleSelectEmployees={handleSelectEmployees}
        />
      </Box>
    </Modal>
  )
}

export default AddBankAccountMembershipModal
